import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client'

import {
  CustomProfileConnectionFragmentDoc,
  CustomProfileEditWallPaperQuery,
  CustomProfileEditWallPaperQueryVariables,
  ItemType,
  Maybe,
  UpdateCustomProfileMutation,
  UpdateCustomProfileMutationVariables,
  UserItemsConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { ItemProps } from '@/components/organisms'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages } = useRouter()

  const { data, fetchMore, networkStatus } = useQuery<
    CustomProfileEditWallPaperQuery,
    CustomProfileEditWallPaperQueryVariables
  >(QUERY_CUSTOM_PROFILE, {
    variables: { organizationPortfoliosType: ItemType.Wallpaper },
    notifyOnNetworkStatusChange: true,
  })

  const items: Maybe<ItemProps[]> =
    data?.user?.organizationPortfolios.edges.map(({ node }) => ({
      id: node.item.id,
      thumbnail: {
        uri: node?.item?.wallpaperResources?.wallpaperResource?.mediumUri ?? '',
      },
    })) ?? []

  const [updateCustomProfile, { loading: customProfileUpdating }] = useMutation<
    UpdateCustomProfileMutation,
    UpdateCustomProfileMutationVariables
  >(UPDATE_CUSTOM_PROFILE)

  return {
    state: {
      loading: networkStatus === NetworkStatus.loading,
      fetching: networkStatus === NetworkStatus.fetchMore,
      customProfileUpdating,
      hasNextPage: !!data?.user?.organizationPortfolios?.pageInfo.hasNextPage,
      items: items,
      default: {
        id: data?.user?.customProfile?.wallpaperItem?.id ?? '',
        thumbnail: {
          uri:
            data?.user?.customProfile?.wallpaperItem?.wallpaperResources
              ?.wallpaperResource?.mediumUri ?? '',
        },
      },
    },
    actions: {
      async onSaveCustomProfile(wallpaperItemId) {
        await updateCustomProfile({
          variables: { updateCustomProfileInputs: { wallpaperItemId } },
        })
        router.goBack(routerPages.MyProfile)
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            organizationPortfoliosType: ItemType.Wallpaper,
            pageInput: {
              cursor: data?.user?.organizationPortfolios?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY_CUSTOM_PROFILE = gql`
  query CustomProfileEditWallPaper(
    $organizationPortfoliosType: ItemType
    $pageInput: PageInput
  ) {
    user {
      id
      organizationPortfolios(
        type: $organizationPortfoliosType
        pageInput: $pageInput
      ) {
        ...UserItemsConnection
      }
      customProfile {
        ...CustomProfileConnection
      }
    }
  }
  ${UserItemsConnectionFragmentDoc}
  ${CustomProfileConnectionFragmentDoc}
`

const UPDATE_CUSTOM_PROFILE = gql`
  mutation UpdateCustomProfile(
    $updateCustomProfileInputs: UpdateCustomProfileInput
  ) {
    updateCustomProfile(inputs: $updateCustomProfileInputs) {
      id
      customProfile {
        ...CustomProfileConnection
      }
    }
  }
  ${CustomProfileConnectionFragmentDoc}
`
